import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, useState } from 'react';
import { useAuthStore } from './stores/auth.store';
import { useQueryClient } from '@tanstack/react-query';
import { ACCOUNT_STATUS } from './@shared/interfaces/user.interfaces';
import { useSyncUserTimezone } from './hooks/useSyncUserTimezone';
import { shallow } from 'zustand/shallow';
import { redirectService } from './pages/redirect/redirectService';

// This will be our authenticated layout wrapper
const AuthenticatedLayout = () => {
  const {
    isAuthenticated,
    user: auth0User,
    isLoading: isAuth0Loading,
    getAccessTokenSilently,
    logout,
  } = useAuth0();

  const {
    setGetToken,
    setGetTokenSilently,
    setLogout,
    fetchProfile,
    profile,
    isLoadingProfile,
    profileError,
    setUser,
    clearAuthAndCache,
  } = useAuthStore(
    (state) => ({
      setGetToken: state.setGetToken,
      setGetTokenSilently: state.setGetTokenSilently,
      setLogout: state.setLogout,
      fetchProfile: state.fetchProfile,
      profile: state.profile,
      isLoadingProfile: state.isLoadingProfile,
      profileError: state.profileError,
      setUser: state.setUser,
      clearAuthAndCache: state.clearAuthAndCache,
    }),
    shallow
  );

  useSyncUserTimezone(isAuthenticated);

  const [initialProfileAttempted, setInitialProfileAttempted] = useState(false);
  const [profileFetchInProgress, setProfileFetchInProgress] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  // Set the token getter function and logout function
  useEffect(() => {
    console.log('[RedirectDebug] 🔄 AuthenticatedLayout component mounted');

    if (getAccessTokenSilently) {
      setGetToken(getAccessTokenSilently);
      setGetTokenSilently(getAccessTokenSilently);
    }

    // Store logout function for API interceptor access
    if (logout) {
      setLogout(logout);
    }
  }, [
    getAccessTokenSilently,
    logout,
    setGetToken,
    setGetTokenSilently,
    setLogout,
  ]);

  // Set basic user info from Auth0 when authenticated
  useEffect(() => {
    if (isAuthenticated && auth0User) {
      // Store basic Auth0 user info in the store - this is just to have some data
      // until the full profile is loaded from the API
      setUser({
        email: auth0User.email || '',
        firstName:
          auth0User?.given_name || (auth0User?.name?.split(' ')[0] ?? ''),
        lastName:
          auth0User?.family_name ||
          (auth0User?.name && auth0User.name.split(' ').length > 1
            ? auth0User.name.split(' ').slice(1).join(' ')
            : ''),
        photoUrl: auth0User.picture,
        auth0Uid: auth0User.sub ? [auth0User.sub] : [],
        canonicalId: '', // Temporary placeholder - will be set when full profile loads
        // Required fields with reasonable defaults
        totalTaskPoints: 0,
        dailyMessageData: {
          count: 0,
          monthCount: 0,
          lastMessageDate: new Date(),
        },
        settings: {
          isOnboarded: false,
          dailyMessageLimit: 0,
          monthlyMessageLimit: 0,
          accountStatus: ACCOUNT_STATUS.FREE,
          sendReminders: false,
          calendars: [],
        },
      });
    }
  }, [isAuthenticated, auth0User, setUser]);

  // SINGLE unified profile fetch effect
  useEffect(() => {
    const { getToken } = useAuthStore.getState();

    // Only fetch profile if ALL conditions are met:
    // 1. User is authenticated
    // 2. Profile isn't already loaded
    // 3. We're not currently loading (in store)
    // 4. We don't have a fetch already in progress (local state guard)
    // 5. Token function is available
    if (
      isAuthenticated &&
      !profile &&
      !isLoadingProfile &&
      !profileFetchInProgress &&
      getToken
    ) {
      setProfileFetchInProgress(true);

      // Add abort controller to cancel request if component unmounts
      const abortController = new window.AbortController();

      fetchProfile()
        .then(() => {})
        .catch((err: any) => {
          // Don't log errors if request was aborted (component unmounted)
          if (!abortController.signal.aborted) {
            console.error('Error fetching profile:', err);
          }
        })
        .finally(() => {
          if (!abortController.signal.aborted) {
            setInitialProfileAttempted(true);
            setProfileFetchInProgress(false);
          }
        });

      // Cleanup function to abort request on unmount
      return () => {
        abortController.abort();
        setProfileFetchInProgress(false);
      };
    }
  }, [
    isAuthenticated,
    profile,
    isLoadingProfile,
    profileFetchInProgress,
    fetchProfile,
  ]);

  // Check authentication status
  useEffect(() => {
    // If auth is done loading and user is not authenticated, redirect to login
    if (!isAuth0Loading && !isAuthenticated) {
      // Store the intended destination before redirecting to login
      // Create a redirect intent for the current protected route
      const redirectIntent = {
        destination: location.pathname,
        source: 'web' as const,
        // Extract tab parameter if it exists
        tab: new URLSearchParams(location.search).get('tab') || undefined,
        params: {},
      };

      console.log('[RedirectDebug] Storing redirect intent:', redirectIntent);
      // Clear any previous redirect completion flag before starting a new flow
      sessionStorage.removeItem('redirect_completed');
      redirectService.storeRedirectIntent(redirectIntent);

      navigate('/login');
      return;
    }

    // Only check profile errors if we've made an initial attempt to load the profile
    // This prevents redirecting to login during initial application load
    if (initialProfileAttempted && !isLoadingProfile && profileError) {
      console.error('Profile error:', profileError);
      // Only log out if there was a serious error like authentication failure
      // Don't log out for network errors or temporary issues
      if (
        profileError.message.includes('Authentication required') ||
        profileError.message.includes('Invalid token')
      ) {
        clearAuthAndCache({
          redirect: true,
          logout,
          queryClient,
          redirectPage: '/login',
        });
      }
    }
  }, [
    isAuth0Loading,
    isAuthenticated,
    location,
    isLoadingProfile,
    profileError,
    initialProfileAttempted,
    navigate,
    logout,
    queryClient,
  ]);

  // Show loading indicator while Auth0 authenticates
  if (isAuth0Loading) {
    return <div>Loading...</div>;
  }

  // If not authenticated, will be redirected in useEffect above
  if (!isAuthenticated) {
    return null;
  }

  // We've verified authentication, so render the app
  // Don't require profile to be loaded - it will be fetched in the background
  return <Outlet />;
};

export default AuthenticatedLayout;
